import { useCallback, useMemo, useState } from 'react'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import {
  PrefixedUserDocumentCategoryIdentifier,
  UserDocumentCategoryIdentifier,
} from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import type { IDocumentsStepContext } from '@/features/bookkeeping/end-of-year-review/types'
import type { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { useDocuments } from '@/features/bookkeeping/hooks/use-documents'
import { useNotRelevantDocuments } from '@/features/bookkeeping/hooks/use-not-relevant-documents'
import { useDocumentReuploadStatus } from '../../../hooks'
import { NotRelevantModal } from '@/features/bookkeeping/end-of-year-review/not-relevant-modal'
import { getInstructionsLink } from '@/features/bookkeeping/end-of-year-review/documents-map'
import { handleDocumentDeletion } from '../document-deletion.helpers'
import './styles.scss'

export interface PayrollDocumentProps {
  stepContext: IDocumentsStepContext | undefined
  setStepContext: (context: IDocumentsStepContext) => void
  provider: string
  userDocuments: UserDocument[]
  isFollowUp: boolean
  taxYear: number
  stepStatus?: string
}

const { formW3 } = UserDocumentCategoryIdentifier

/**
 * PayrollDocument
 *
 * Reusable component for managing payroll document uploads for a specific provider.
 * This is a provider-based document type that handles multiple payroll-related documents.
 *
 * Features:
 * - Provider-based document type (dynamically detected, NOT hardcoded)
 * - Multiple documents: Form W-3, Payroll Journal, Form W-2s, Quarterly reports
 * - Embedded FileUploadModal for uploads
 * - "Not relevant" workflow support via useNotRelevantDocuments hook
 * - Uses existing V1 UI components (DocumentUploadCard, FileUploadModal)
 * - Uses existing useDocuments hook for filtering and status utilities
 *
 * Provider-based Pattern:
 * - ALWAYS pass provider parameter to filterDocuments() and getStatus()
 * - Provider is dynamically detected (e.g., "Heard", "Gusto", "ADP", "Quickbooks")
 * - Documents are tagged with provider field for proper categorization
 *
 * Document Types Handled:
 * - Form W-3: Summarizes business' total salary payments and withholding amounts
 * - Payroll Journal: Summary of gross wages, employee taxes, employer taxes, net pay
 * - Form W-2s: Individual employee wage and tax statements
 * - Quarterly payroll reports: Q1-Q4 payroll tax reports
 *
 * @example
 * ```tsx
 * <PayrollDocument
 *   stepContext={payrollContext}
 *   setStepContext={setPayrollContext}
 *   provider="Gusto"  // Dynamic provider, NOT hardcoded
 *   userDocuments={allUserDocuments}
 *   isFollowUp={false}
 *   taxYear={2024}
 * />
 * ```
 */
export function PayrollDocument({
  stepContext,
  setStepContext,
  provider,
  userDocuments,
  isFollowUp,
  taxYear,
  stepStatus,
}: PayrollDocumentProps) {
  const [uploadDialog, setUploadDialog] = useState(false)
  const [uploadMetadata, setUploadMetadata] = useState<{
    type?: UploadDocumentType
    categoryCode: string
    provider: string
  } | null>(null)

  // EXISTING HOOK: Provides utility functions for document filtering & status
  const { filterDocuments, getStatus } = useDocuments({
    stepContextDocuments: stepContext?.documents,
    userDocuments,
  })

  // NEW HOOK: Manages "not relevant" workflow with modal state
  const { isMarkedNotRelevant, handleNotRelevant, notRelevantModal } =
    useNotRelevantDocuments(stepContext, setStepContext)

  // Payroll Journal is year-specific
  const payrollJournal = useMemo(
    () =>
      PrefixedUserDocumentCategoryIdentifier.payrollJournal(taxYear.toString()),
    [taxYear]
  )

  // Get re-upload status for each document type
  const w3ReuploadStatus = useDocumentReuploadStatus(
    stepContext,
    formW3,
    provider,
    isFollowUp
  )
  const payrollJournalReuploadStatus = useDocumentReuploadStatus(
    stepContext,
    payrollJournal,
    provider,
    isFollowUp
  )

  const handleUploadClick = useCallback(
    (categoryCode?: string, type?: UploadDocumentType) => {
      if (categoryCode) {
        setUploadMetadata({
          categoryCode,
          provider,
          type,
        })
      } else {
        setUploadMetadata(null)
      }
      setUploadDialog(true)
    },
    [provider]
  )

  const handleFileUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      if (!uploadMetadata) {
        // Handle "Other documents" upload
        const otherDocumentIds = [
          ...(stepContext?.otherDocumentIds ?? []),
          uploadedFile.id,
        ]
        setStepContext({ ...(stepContext ?? {}), otherDocumentIds })
        setUploadDialog(false)
        return
      }

      const { categoryCode } = uploadMetadata

      // Find the original document that needs re-upload (if it exists)
      const originalDocument = stepContext?.documents?.find(
        (doc) => doc.categoryCode === categoryCode && doc.provider === provider
      )

      // Replace or add the document to the list
      // Preserve requestReUpload and adminNote from original, set updatedByUser: true
      const documents = [
        ...(stepContext?.documents?.filter(
          (doc) =>
            !(doc.categoryCode === categoryCode && doc.provider === provider)
        ) ?? []),
        {
          documentId: uploadedFile.id,
          categoryCode,
          provider,
          adminNote: originalDocument?.adminNote,
          requestReUpload:
            originalDocument?.requestReUpload ??
            (isFollowUp ? true : undefined),
          updatedByUser: true,
        },
      ]

      setStepContext({ ...(stepContext ?? {}), documents })
      setUploadDialog(false)
      setUploadMetadata(null)
    },
    [uploadMetadata, stepContext, setStepContext, provider, isFollowUp]
  )

  // Handle document deletion - preserve requestReUpload state in follow-up mode
  const handleDocumentDeleted = useCallback(
    (deletedDocumentId: number) => {
      const documents = handleDocumentDeletion(
        stepContext?.documents,
        (doc) => doc.documentId === deletedDocumentId,
        isFollowUp,
        { preserveProvider: true }
      )
      setStepContext({ ...(stepContext ?? {}), documents })
    },
    [stepContext, setStepContext, isFollowUp]
  )
  // For followup: Check if step requires user action - only render section if status is user_action_required
  if (isFollowUp && stepStatus !== 'user_action_required') {
    return null
  }

  return (
    <>
      <div className="payroll-document">
        <h2>Payroll</h2>

        <div className="payroll-document__card-container">
          {/* Form W-3 */}
          <DocumentUploadCard
            name={
              provider === 'Other'
                ? 'Other - W-2 Package (if electronic) or W-3 (if paper)'
                : `${provider} - W-2 Package`
            }
            description="This summarizes your business' total salary payments and withholding amounts."
            href={getInstructionsLink(formW3, provider, taxYear.toString())}
            status={getStatus(formW3, provider, isMarkedNotRelevant)}
            documents={filterDocuments(formW3, provider)}
            handleUploadBtn={() =>
              handleUploadClick(formW3, UploadDocumentType.TAX)
            }
            notRelevant={isMarkedNotRelevant(formW3, provider)}
            setChecked={() => handleNotRelevant(formW3, provider)}
            isFollowUp={isFollowUp}
            isRequiredReviewed={w3ReuploadStatus}
            onDocumentDeleted={handleDocumentDeleted}
          />

          {/* Payroll Journal */}
          <DocumentUploadCard
            name={`${provider} - Payroll Journal`}
            description="Includes a summary of gross wages, employee taxes, employer taxes, net pay."
            href={getInstructionsLink(
              payrollJournal,
              provider,
              taxYear.toString()
            )}
            status={getStatus(payrollJournal, provider, isMarkedNotRelevant)}
            documents={filterDocuments(payrollJournal, provider)}
            handleUploadBtn={() =>
              handleUploadClick(payrollJournal, UploadDocumentType.BOOKKEEPING)
            }
            notRelevant={isMarkedNotRelevant(payrollJournal, provider)}
            setChecked={() => handleNotRelevant(payrollJournal, provider)}
            isFollowUp={isFollowUp}
            isRequiredReviewed={payrollJournalReuploadStatus}
            onDocumentDeleted={handleDocumentDeleted}
          />

          {/* Additional payroll documents can be added here:
              - Form W-2s
              - Quarterly payroll reports (Q1-Q4)
              - etc.
          */}

          {/* Other documents */}
          <DocumentUploadCard
            name="Other documents"
            description="Upload any other documents you'd like to share with the bookkeeping team here."
            status="disabled"
            documents={(userDocuments ?? []).filter((doc) =>
              (stepContext?.otherDocumentIds ?? []).includes(doc.id)
            )}
            handleUploadBtn={() => handleUploadClick()}
          />
        </div>
      </div>

      {/* Embedded V1 FileUploadModal specific to payroll documents */}
      <FileUploadModal
        open={uploadDialog}
        close={() => setUploadDialog(false)}
        documentType={uploadMetadata?.type || UploadDocumentType.OTHER}
        categoryInternalName={uploadMetadata?.categoryCode}
        userFacing
        year={taxYear.toString()}
        setUploadedFile={handleFileUploaded}
        isUploadOnly
      />

      {/* Not relevant confirmation modal */}
      <NotRelevantModal
        isOpen={notRelevantModal.isOpen}
        handleClose={notRelevantModal.handleClose}
        setConfirmNotRelevant={notRelevantModal.setConfirmNotRelevant}
      />
    </>
  )
}
